import type { ComponentSize } from 'src/modules/shared/types/component.type';
import type {
  AvatarProps,
  AvatarStatusVariant,
} from 'src/modules/shared/types/avatar.type';
import { CircleDisplay } from '../circle-display';
import { useState } from 'react';

export const Avatar: React.FC<AvatarProps> = ({
  isOnline,
  onClick,
  status,
  ...props
}) => {
  const [isStatusExpanded, setIsStatusExpanded] = useState(false);
  const statusSizeClasses: Record<ComponentSize, string> = {
    '2xs': 'w-1.5 h-1.5',
    xs: 'w-2 h-2',
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
  };
  const statusColorClasses: Record<AvatarStatusVariant, string> = {
    neutral: 'bg-gray-400',
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
  };
  const statusVariant =
    status?.variant ??
    (isOnline === undefined ? undefined : isOnline ? 'success' : 'neutral');
  const statusClassName = statusVariant
    ? statusColorClasses[statusVariant]
    : undefined;
  const statusDotClassName = `
    ${statusSizeClasses[props.size ?? 'md']}
    rounded-full
    border-2
    border-white
    ${statusClassName ?? ''}
    ${props.disabled ? 'opacity-50' : ''}
  `;

  return (
    <div className="relative">
      <CircleDisplay {...props} onClick={onClick} />
      {statusVariant &&
        (status?.label ? (
          <span className="absolute bottom-0 right-0">
            <button
              type="button"
              title={status.label}
              aria-label={status.label}
              aria-expanded={isStatusExpanded}
              className={`${statusDotClassName} block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700`}
              onClick={(event) => {
                event.stopPropagation();
                setIsStatusExpanded((currentValue) => !currentValue);
              }}
              onBlur={() => setIsStatusExpanded(false)}
            />

            {isStatusExpanded && (
              <span className="absolute right-0 top-full z-30 mt-1 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-lg">
                {status.label}
              </span>
            )}
          </span>
        ) : (
          <div className={`absolute bottom-0 right-0 ${statusDotClassName}`} />
        ))}
    </div>
  );
};
